import React, {useEffect} from 'react';
import {Alert, Button, Col, Form, Modal, Row, Tab, Tabs} from "react-bootstrap";
import {ADMIN_LANG, AVAILABLE_LANGUAGES} from "../../../settings";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {
    changeGameModal,
    closeGameModal, deleteGameLanguage, getGameDetails,
    NewGamePayload, saveGame,
    saveNewGame, UpdateGamePayload
} from "../../../app/slices/modals/gameEditModal";
import {useNavigate} from 'react-router-dom';
import AdminLoader from "../AdminLoader";
import {setActiveLanguage} from "../../../app/slices/modals/gameEditModal";
import CommonFormFields from "../modals/gameEditModal/CommonFormFields";
import Confirm from "../Confirm";
import {ExclamationTriangleFill} from "react-bootstrap-icons";

const GameEditModal = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        isOpen,
        game_id,
        current_version_id,
        error,
        languages,
        loading,
        languageTab,
        active_languages,
        display_correct_answers,
        display_end_screen_points,
        min_points,
        end_points,
        teams,
        form,
        end_form,
        restrict_playing,
        show_lift,
        theme,
        notification_emails
    } = useAppSelector((state) => state.gameEditModal);
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const handleTabSelect = (key: string | null) => {
        if (key) {
            dispatch(setActiveLanguage(key));
        }
    };

    useEffect(() => {
        // NOTE: on React.StrictMode, this useEffect is called twice on dev mode
        if (game_id && current_version_id) {
            dispatch(getGameDetails({game_id: game_id, version_id: current_version_id}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, game_id, current_version_id]);


    const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const modalForm = e.currentTarget;

        if (!modalForm.checkValidity()) {
            e.stopPropagation();
        } else {
            if (game_id && current_version_id) {
                // Update
                const payload: UpdateGamePayload = {
                    game_id,
                    version_id: current_version_id,
                    languages,
                    min_points,
                    teams,
                    display_correct_answers,
                    display_end_screen_points,
                    restrict_playing,
                    end_points,
                    form,
                    end_form,
                    show_lift,
                    theme,
                    notification_emails
                };
                dispatch(saveGame(payload));

            } else {
                const payload: NewGamePayload = {
                    languages,
                    display_correct_answers,
                    display_end_screen_points
                };
                dispatch(saveNewGame(payload)).then((resultAction) => {
                    if (saveNewGame.fulfilled.match(resultAction)) {
                        const {game_id, current_version_id} = resultAction.payload;
                        if (game_id && current_version_id) {
                            navigate(`/admin/games/${game_id}/versions/${current_version_id}/`);
                        }
                    }
                });
            }
        }
    };
    const handleClose = () => {
        dispatch(closeGameModal());
    };

    const handleDelete = () => {
        setShowConfirmDelete(true);
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if(game_id && current_version_id) {
            dispatch(deleteGameLanguage({game_id, version_id: current_version_id, language: languageTab}));
        }
    };

    const handleLanguageSelect = (language: string) => {
        if (language) {
            dispatch(changeGameModal({active_languages: [language], languageTab: language}));
        }
    }


    let gameTitle;
    if (game_id && current_version_id) {
        const game = languages.find(lang => lang.language_code === languageTab);
        gameTitle = game?.name || '';
    } else {
        gameTitle = ADMIN_LANG.new_game;
    }

    const currentLanguage = languages.find(lang => lang.language_code === languageTab);
    const currentLanguageIsActive = currentLanguage?.is_active || false;

    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            {loading && <AdminLoader/>}
            <Modal.Header closeButton>
                <Modal.Title>{gameTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{__html: error}}/>
                    </Alert>
                )}
                {currentLanguageIsActive &&
                    <p className='text-info fw-semibold'>
                        <ExclamationTriangleFill className="me-2"/>
                        {ADMIN_LANG.is_active_info}
                    </p>
                }
                <Form id='game-modal-form' onSubmit={handleSave}>
                    <Row className='gy-3'>
                        <Col xs={12}>
                            {active_languages.length === 0 ? (
                                <Form.Group controlId='game-language'>
                                    <Form.Label>{ADMIN_LANG.select_language}</Form.Label>
                                    <Form.Select onChange={(e) => handleLanguageSelect(e.target.value)}>
                                        <option value=''>{ADMIN_LANG.select_language}</option>
                                        {AVAILABLE_LANGUAGES.map((language) => (
                                            <option value={language}
                                                    key={language}>{language.toUpperCase()}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            ) : (
                                <Tabs className='mb-3' activeKey={languageTab} onSelect={handleTabSelect}>
                                    {active_languages.map(language => (
                                        <Tab eventKey={language} title={language.toUpperCase()} key={language}>
                                            <CommonFormFields
                                                language={language}
                                                newGame={!game_id}
                                            />
                                        </Tab>
                                    ))}
                                </Tabs>
                            )}
                        </Col>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {game_id &&
                    <Button variant='danger' onClick={handleDelete} className='me-auto'>
                        {ADMIN_LANG.delete_language_version}: {languageTab.toUpperCase()}
                    </Button>
                }
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant='primary' form='game-modal-form' type='submit'>
                    {ADMIN_LANG.save}
                </Button>
            </Modal.Footer>
            <Confirm
                show={showConfirmDelete}
                title={`${ADMIN_LANG.delete} ${ADMIN_LANG.game_language_version}`}
                message={ADMIN_LANG.confirm_delete_game_language}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmDelete(false)}
            />
        </Modal>
    );
}

export default GameEditModal;